import type { PropsWithChildren } from 'react'
import { useCMSPageField } from '@/cms/fields'
import { type CMSMedia, CMSMediaType } from '@/cms/fields/mappers/productDetailPageFieldsMapper'
import { useOptimizelyFlagContext } from '@/scopes/common/infrastructure/ab-testing/useOptimizely'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { createPDPClickEvent } from '@/scopes/products/infrastructure/analytics'
import { Button, Icon, Modal, tk, zIndex } from '@sunwebgroup/ui-components'
import useEmblaCarousel from 'embla-carousel-react'
import { useCallback, useEffect, useState } from 'react'
import { css, styled } from 'styled-components'
import { DetailImage } from './DetailImage'
import { Video } from './Video'

export type DetailProps = PropsWithChildren<Omit<Modal.ModalProps, '$size' | 'title'>> & { startFromIndex?: number }

const StyledPlay = styled.div`
  position: absolute;
  bottom: 50%;
  right: 50%;
  transform: translate(50%, 50%);
  background-color: ${tk('color_palette_transparent_white_75')};
  border-radius: ${tk('radius_rounded')};
`

const StyledPlayIcon = styled(Icon)`
  width: auto;
  padding: ${tk('spacing_sm')};
  position: relative;
  left: 2px; // To center the icon
`

const StyledVideoButton = styled.button.attrs<{ $visible: boolean }>(props => ({ $visible: props.$visible }))`
  display: ${({ $visible }) => ($visible ? 'block' : 'none')};
  height: 100%;
  width: 100%;
  position: relative;

  img {
    width: 100%;
    display: block;
  }

  &:hover {
    img {
      filter: brightness(0.85);
      transition: all 0.3s ease-in-out;
    }

    ${StyledPlay} {
      background-color: ${tk('color_semantic_bg')};
      color: ${tk('color_semantic_icon_hover')};
      transition: all 0.3s ease-in-out;
    }
  }

  &:not(:hover) {
    img {
      transition: all 0.3s ease-in-out;
    }

    ${StyledPlay} {
      transition: all 0.3s ease-in-out;
    }
  }
`

const StyledVideo = styled(Video).attrs<{ $visible: boolean }>(props => ({
  $visible: props.$visible,
}))`
  ${({ $visible }) =>
    $visible
      ? css`
          display: block;
        `
      : css`
          display: none;
        `};
`

const StyledModalHeader = styled(Modal.Header)`
  background: ${tk('color_semantic_bg_inverse_hover')};
  color: ${tk('color_semantic_text_on_color')};
  height: ${tk('spacing_4xl')};

  * {
    color: inherit;
  }
`

const StyledModal = styled(Modal.Content)`
  background: ${tk('color_semantic_bg_inverse_hover')};
  z-index: ${zIndex.modalOrMobilePopover + 50} !important;
  padding-bottom: ${tk('spacing_4xl')};

  ${Modal.Body} {
    @media ${tk('mq_tablet')} {
      padding-left: ${tk('spacing_xl')};
      padding-right: ${tk('spacing_xl')};
    }

    @media ${tk('mq_desktop_sm')} {
      padding-left: ${tk('spacing_5xl')};
      padding-right: ${tk('spacing_5xl')};
    }
  }
`

const StyledButton = styled(Button)`
  display: none;

  @media ${tk('mq_desktop_sm')} {
    display: inline-flex;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
  }
`

const StyledLeftButton = styled(StyledButton)`
  left: 16px;

  // This is because the icon is causing wrong target for Google Analytics to be tracked
  ${Icon} {
    pointer-events: none;
  }
`

const StyledRightButton = styled(StyledButton)`
  right: 16px;

  // This is because the icon is causing wrong target for Google Analytics to be tracked
  ${Icon} {
    pointer-events: none;
  }
`

const Container = styled.section`
  position: relative;
  height: 100%;
`

const Slider = styled.div`
  overflow: hidden;
  height: 100%;
`

const SliderContainer = styled.div`
  display: flex;
  height: 100%;
`

const Slide = styled.div`
  flex: 0 0 100%;

  iframe {
    height: 100%;
    width: 100%;
  }

  img {
    object-fit: contain;
    height: 100%;

    //To be discussed: blurry images of placeholder are overflowing the container.
    background-color: ${tk('color_semantic_bg_inverse_hover')};
  }
`

export function Detail({ startFromIndex = 0, ...attrs }: DetailProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, startIndex: startFromIndex })
  const { noGalleryVideosDecision } = useOptimizelyFlagContext()
  const { sendEvent } = useAnalytics()
  const [slidesInView, setSlidesInView] = useState<number[]>([])
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0)
  const [activeVideo, setActiveVideo] = useState(false)
  const allMedia = useCMSPageField('PDP.media')
  const media: CMSMedia[] = []
  for (const item of allMedia) {
    if (item.type !== CMSMediaType.VIDEO || !noGalleryVideosDecision) {
      media.push(item)
    }
  }
  const numberOfSlides = media.length

  const findSlidesInView = useCallback(() => {
    if (!emblaApi)
      return

    setSlidesInView((slidesInViewElements) => {
      if (slidesInViewElements.length === emblaApi.slideNodes().length) {
        emblaApi.off('slidesInView', findSlidesInView)
      }
      const inView = emblaApi.slidesInView().filter(index => !slidesInViewElements.includes(index))
      return slidesInViewElements.concat(inView)
    })
  }, [emblaApi, setSlidesInView])

  function navigate(direction: 'previous' | 'next') {
    if (direction === 'previous') {
      emblaApi?.scrollPrev()
    }
    else {
      emblaApi?.scrollNext()
    }
  }

  // Slider events
  const onSelect = useCallback(() => {
    if (emblaApi !== undefined) {
      const index = emblaApi.selectedScrollSnap()
      setCurrentSlideIndex(index)
    }
  }, [emblaApi])

  const onScroll = useCallback(() => {
    setActiveVideo(false)
  }, [])

  const onSettle = useCallback(() => {
    sendEvent(
      createPDPClickEvent({
        element: 'Slider',
        description: `Photo Gallery`,
        result: 'Image slider - Slide',
      }),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps -- We know that sendClickPdpEvent is not changing
  }, [])

  const showVideo = () => {
    setActiveVideo(true)
  }

  useEffect(() => {
    if (emblaApi === undefined)
      return

    setCurrentSlideIndex(emblaApi.selectedScrollSnap())
    emblaApi.on('select', onSelect)
    emblaApi.on('scroll', onScroll)
    emblaApi.on('settle', onSettle)
    emblaApi.on('slidesInView', findSlidesInView)
    emblaApi.reInit()

    return () => {
      emblaApi.off('select', onSelect)
      emblaApi.off('scroll', onScroll)
      emblaApi.off('settle', onSettle)
      emblaApi.off('slidesInView', findSlidesInView)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only run once embla is mounted
  }, [emblaApi])

  const handlePauseOrEnded = () => {
    setActiveVideo(false)
  }

  const handleClose = () => {
    setActiveVideo(false)
    attrs.onClose?.()
  }

  return (
    <Modal.Root {...attrs} onClose={handleClose}>
      <StyledModal $size="fullscreen">
        <StyledModalHeader>
          <Modal.HeaderTitle text={`${currentSlideIndex + 1}  / ${numberOfSlides}`} as="span" />
          <Modal.HeaderCloseButton />
        </StyledModalHeader>
        <Modal.Body>
          <Container>
            <Slider ref={emblaRef}>
              <SliderContainer>
                {media.map((mediaItem, index) => {
                  if (mediaItem.type === CMSMediaType.VIDEO) {
                    return (
                      <Slide key={index}>
                        <StyledVideo
                          link={mediaItem.link || ''}
                          playing={activeVideo}
                          onPauseOrEnded={handlePauseOrEnded}
                          $visible={activeVideo}
                        />
                        <StyledVideoButton onClick={showVideo} $visible={!activeVideo}>
                          <DetailImage src={mediaItem.src} alt={mediaItem.alt} inView={slidesInView.includes(index)} />

                          <StyledPlay>
                            <StyledPlayIcon glyph="IconPlay" size="md" aria-hidden />
                          </StyledPlay>
                        </StyledVideoButton>
                      </Slide>
                    )
                  }

                  return (
                    <Slide key={index}>
                      <DetailImage src={mediaItem.src} alt={mediaItem.alt} inView={slidesInView.includes(index)} />
                    </Slide>
                  )
                })}
              </SliderContainer>
            </Slider>
          </Container>
          {numberOfSlides > 0 && (
            <>
              <StyledLeftButton
                $hierarchy="secondary"
                $withoutColor
                onClick={() => navigate('previous')}
                disabled={currentSlideIndex === 0}
              >
                <Icon glyph="IconChevronLeft" />
              </StyledLeftButton>
              <StyledRightButton
                $hierarchy="secondary"
                $withoutColor
                onClick={() => navigate('next')}
                disabled={currentSlideIndex === numberOfSlides - 1}
              >
                <Icon glyph="IconChevronRight" />
              </StyledRightButton>
            </>
          )}
        </Modal.Body>
      </StyledModal>
    </Modal.Root>
  )
}
